import { useEffect, useState } from "react";
import { Star, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { VideoPlayer } from "@/components/VideoPlayer";
import { GoogleReviewQRCard } from "@/components/GoogleReviewQR";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";

export const FaqSection = () => {
  const { site } = useSite() || {};
  const faqs = site?.faqs || [];
  if (!faqs.length) return null;
  return (
    <section className="container-x grid gap-10 py-20 lg:grid-cols-[1fr,1.6fr]" aria-labelledby="faq-title" data-testid="faq-section">
      <div>
        <p className="eyebrow">Preguntas frecuentes</p>
        <h2 id="faq-title" className="mt-3 text-3xl sm:text-4xl font-extrabold">Resolvemos tus <span className="text-metal">dudas</span></h2>
        <p className="mt-4 text-mute">¿No encuentras tu pregunta? Llámanos al 999-191-9300 o escríbele a nuestro asistente.</p>
      </div>
      <Accordion type="single" collapsible className="card-dark px-6">
        {faqs.map((f, i) => (
          <AccordionItem key={f.id} value={f.id} className="border-line" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-display text-base font-bold hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-ink2 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export const Stars = ({ value, size = 16 }) => (
  <div className="flex gap-0.5 text-gold" aria-label={`${value} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} fill={n <= value ? "currentColor" : "none"} />)}
  </div>
);

export const ReviewCard = ({ r }) => (
  <article className="card-dark flex flex-col p-6" data-testid={`review-${r.id}`}>
    <div className="flex items-center justify-between"><Stars value={r.rating} />{r.source === "google" && <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-bold text-mute">Reseña de Google</span>}</div>
    <p className="mt-4 flex-1 text-ink2">“{r.comment}”</p>
    <p className="mt-4 font-display font-bold">{r.name}{r.vehicle && <span className="text-sm font-normal text-mute"> · {r.vehicle}</span>}{r.review_date && <span className="text-xs font-normal text-mute2"> · {r.review_date}</span>}</p>
  </article>
);

export const LatestVideos = () => {
  const [videos, setVideos] = useState([]);
  useEffect(() => { api.get("/videos?category=instructivo").then((r) => setVideos([r.data[0], r.data[5] || r.data[1], r.data[2]].filter(Boolean))).catch(() => {}); }, []);
  if (!videos.length) return null;
  return (
    <section className="container-x py-16" aria-labelledby="latest-videos-title" data-testid="latest-videos-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Trabajos realizados</p>
          <h2 id="latest-videos-title" className="mt-3 text-3xl sm:text-4xl font-extrabold">Mira cómo <span className="text-metal">trabajamos</span></h2>
        </div>
        <Link to="/galeria" className="btn-ghost self-start" data-testid="home-gallery-btn">Ver toda la galería <ArrowRight size={16} /></Link>
      </div>
      <div className="mt-10 grid items-start gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {videos.map((v) => (
          <article key={v.id} className="card-dark overflow-hidden" data-testid={`home-video-${v.id}`}>
            <VideoPlayer v={v} />
            {(v.title || v.description) && <div className="p-5">{v.title && <h3 className="font-display text-lg font-bold">{v.title}</h3>}{v.description && <p className="mt-1 text-sm text-mute">{v.description}</p>}</div>}
          </article>
        ))}
      </div>
    </section>
  );
};

export const useGoogleReviews = () => {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/google-reviews").then((r) => setG(r.data)).catch(() => setG({ configured: false })); }, []);
  return g;
};

export const GoogleReviews = ({ g, limit = 6 }) => {
  if (!g?.configured || !g.reviews?.length) return null;
  return (
    <div data-testid="google-reviews">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display text-4xl font-extrabold">{g.rating?.toFixed(1)}</span>
        <Stars value={Math.round(g.rating || 0)} size={20} />
        <span className="text-sm text-mute">{g.count} reseñas en Google</span>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {g.reviews.slice(0, limit).map((r, i) => (
          <article key={i} className="card-dark flex flex-col p-6" data-testid={`google-review-${i}`}>
            <div className="flex items-center justify-between"><Stars value={r.rating} /><span className="text-[10px] font-bold text-mute">Google</span></div>
            <p className="mt-4 flex-1 text-ink2">“{r.text}”</p>
            <div className="mt-4 flex items-center gap-3">
              {r.photo && <img src={r.photo} alt="" className="h-9 w-9 rounded-full" referrerPolicy="no-referrer" />}
              <div>
                {r.author_uri ? <a href={r.author_uri} target="_blank" rel="noopener noreferrer" className="font-display font-bold hover:underline">{r.author}</a> : <p className="font-display font-bold">{r.author}</p>}
                <p className="text-xs text-mute2">{r.when}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
      <p className="mt-3 text-xs text-mute2">Reseñas obtenidas de Google Maps.</p>
    </div>
  );
};

export const FeaturedReviews = () => {
  const g = useGoogleReviews();
  const { site } = useSite() || {};
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/reviews?featured=true").then((r) => setItems(r.data.slice(0, 6))).catch(() => {}); }, []);
  const b = site?.business;
  return (
    <section className="bg-surface py-20" aria-labelledby="featured-reviews-title" data-testid="featured-reviews-section">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Opiniones de clientes</p>
            <h2 id="featured-reviews-title" className="mt-3 text-3xl sm:text-4xl font-extrabold">Lo que dicen <span className="text-metal">nuestros clientes</span></h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={b?.google_review_url || "https://g.page/r/CQ2jiTZJRmLlEAI/review"} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="google-review-primary-button"><Star size={16} /> Dejar opinión en Google</a>
            <a href={b?.google_maps_url} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="home-read-google-reviews-btn"><ExternalLink size={16} /> Leer reseñas en Google</a>
          </div>
        </div>
        {g?.configured && g.reviews?.length ? (
          <div className="mt-10"><GoogleReviews g={g} limit={3} /></div>
        ) : items.length > 0 ? (
          <div className="mt-10 grid gap-5 md:grid-cols-3">{items.map((r) => <ReviewCard key={r.id} r={r} />)}</div>
        ) : (
          <p className="mt-8 text-mute" data-testid="featured-reviews-empty">Pronto compartiremos aquí las opiniones de nuestros clientes. ¿Ya nos visitaste? Cuéntanos cómo te fue en Google.</p>
        )}
        <div className="mt-12"><GoogleReviewQRCard id="home-qr" /></div>
      </div>
    </section>
  );
};
